import { useEffect, useLayoutEffect, useRef, useState } from "react";
/** 브레이크포인트 경계(px). 원본은 뷰포트 기준 미디어쿼리였다. */
const BREAKPOINTS = [
    ["wide", 1440],
    ["desktop", 1024],
    ["tablet", 768],
];
function widthToBreakpoint(w) {
    for (const [bp, min] of BREAKPOINTS)
        if (w >= min)
            return bp;
    return "mobile";
}
/**
 * 1440 / 1024 / 768 브레이크포인트 — **컨테이너 너비** 기준.
 *
 * 원본(dataflow-monitor)은 대시보드가 곧 뷰포트 전체라 window.matchMedia 로 충분했다.
 * SMI 안에서는 좌측 사이드바(60px 접힘 / 280px 펼침)를 뺀 영역만 쓰므로 뷰포트로 재면
 * 실제보다 넓다고 판단해 3패널을 펼치고 우측 요약 패널이 화면 밖으로 밀린다.
 * → ResizeObserver 로 실제 컨테이너를 잰다. 사이드바 접기/펼치기에도 자동으로 반응한다.
 *
 * @param {{current: HTMLElement|null}} ref 대시보드 루트 엘리먼트
 */
export function useBreakpoint(ref) {
    const [bp, setBp] = useState("wide");
    useLayoutEffect(() => {
        const el = ref?.current;
        if (!el) {
            setBp(widthToBreakpoint(typeof window === "undefined" ? 1440 : window.innerWidth));
            return;
        }
        const update = (w) => setBp((prev) => {
            const next = widthToBreakpoint(w);
            return next === prev ? prev : next;
        });
        update(el.getBoundingClientRect().width);
        const ro = new ResizeObserver((entries) => {
            for (const e of entries)
                update(e.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return bp;
}
/** 1초마다 갱신되는 현재 시각(ms). 상대시간 표시용. 마운트 전에는 null (hydration 안전). */
export function useNow(intervalMs = 1000) {
    const [now, setNow] = useState(null);
    useEffect(() => {
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), intervalMs);
        return () => clearInterval(id);
    }, [intervalMs]);
    return now;
}
/** 콜백 디바운스 (언마운트 시 타이머 정리) */
export function useDebouncedCallback(fn, delayMs) {
    const fnRef = useRef(fn);
    fnRef.current = fn;
    const timer = useRef(null);
    useEffect(() => () => {
        if (timer.current)
            clearTimeout(timer.current);
    }, []);
    return (v) => {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = setTimeout(() => fnRef.current(v), delayMs);
    };
}
